import { useGlobalQuery } from 'state/query-client/hooks';
import { listDatabaseConnections, DatabaseConnectionListItem } from 'services/database-connection-service';
import { useConfig } from './use-config';

/**
 * Custom hook to fetch list of database connections (simplified view)
 *
 * This hook provides a query function to fetch a simplified list of database connections
 * with active/inactive status. This is optimized for list views and checking connection status.
 * It uses the global query pattern for consistent error handling and authentication management.
 *
 * Features:
 * - Automatic configuration injection
 * - Global error handling with authentication management
 * - Loading state management
 * - Caching and refetching support
 * - TypeScript support for response types
 * - Lightweight response (no detailed connection info)
 *
 * @returns {Object} The query object with data and state
 * @returns {DatabaseConnectionListItem[] | undefined} data - The list of database connections (simplified)
 * @returns {boolean} isLoading - Whether the query is loading
 * @returns {boolean} isSuccess - Whether the query completed successfully
 * @returns {boolean} isError - Whether the query failed
 * @returns {Error | null} error - The error if query failed
 * @returns {Function} refetch - Function to manually refetch the data
 *
 * @example
 * const { data: connectionsList, isLoading, refetch } = useListDatabaseConnections();
 * 
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 * 
 * return (
 *   <div>
 *     {connectionsList?.map(conn => (
 *       <ConnectionCard 
 *         key={conn.ConnectionId} 
 *         connection={conn} 
 *         showConnected={conn.DatabaseType === 'postgres' && conn.IsActive}
 *       />
 *     ))}
 *   </div>
 * );
 */
export const useListDatabaseConnections = () => {
    const { configurationSettings } = useConfig();

    return useGlobalQuery<DatabaseConnectionListItem[]>({
        queryKey: ['listDatabaseConnections'],
        queryFn: async () => {
            if (!configurationSettings) {
                throw new Error('Configuration settings are not available');
            }
            return await listDatabaseConnections(configurationSettings);
        },
        enabled: !!configurationSettings,
        staleTime: 30000, // 30 seconds
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });
};

